import { axialHexDistance } from '../../hexmap/coords';
import { getHexTileMapKey } from '../../hexmap/model/keys';
import { type TickDerived, tickDerived } from '../derived';
import { cityRadius } from '../levels';
import { redirect } from '../redirect';
import { isHidden } from '../spies';
import { holdsStock } from '../stock';
import { mintOrder } from '../territory';
import type { Building, EntityId, PlayerId, SimContext, SimState, SystemFn, Unit } from '../types';
import { canAttack, canHit } from './pursuit';

/** How far (hexes) an idle defender goes for an attacker of its city. */
const GUARD_RADIUS = 6;
/** How far past its city's territory (hexes) a defender follows an attacker. */
const GUARD_LEASH = 2;

function idNumber(id: EntityId): number {
  return Number.parseInt(id.slice(1), 10);
}

/**
 * Defenders guard their city: an idle own fighter (no order, no path, not
 * automated) standing in the territory of an own completed city attacks the
 * nearest enemy unit within GUARD_RADIUS that is attacking that city — one
 * that damaged its owner in the last few seconds (`PlayerState.revealed`,
 * see combat.ts), which they can see, standing within the city's territory
 * radius + GUARD_LEASH of it — so units guarding a city answer artillery
 * shelling it from out of its fire instead of watching it fall. The order is
 * an attack order marked `guard` (the city's id); each tick, a guard whose
 * target is gone, out of sight or past the leash takes the next attacker in
 * reach, else drops the order and stands where it is. Units stored in a
 * barracks come out by the storage rules (an enemy fighter in the
 * territory, or the city hit) and then guard like any idle unit. Only
 * aggressive units answer (`Unit.stance`): a hold or passive one is never
 * recruited and drops a guard order. Runs after storage, before pursuit
 * (which then closes them in).
 */
const guard: SystemFn = (state, ctx) => {
  const players = Object.keys(state.players).filter(
    (p) => state.players[p]?.revealed || hasGuards(state, p)
  );
  if (players.length === 0) return;
  const derived = tickDerived(state, ctx);
  for (const player of players.sort()) guardFor(state, ctx, player, derived);
};

function hasGuards(state: SimState, player: PlayerId): boolean {
  for (const u of Object.values(state.units)) {
    if (u.owner === player && u.order?.kind === 'attack' && u.order.guard !== undefined)
      return true;
  }
  return false;
}

function guardFor(state: SimState, ctx: SimContext, player: PlayerId, derived: TickDerived): void {
  const owners = derived.owners();
  const revealed = state.players[player]?.revealed ?? {};
  const seen = (u: Unit) => derived.vision(player).has(getHexTileMapKey(u.pos));
  // Within the city's reach: its territory radius + the leash.
  const nearCity = (city: Building, u: Unit) =>
    axialHexDistance(city.pos, u.pos) <= (cityRadius(ctx.rules, city) ?? 0) + GUARD_LEASH;
  const enemies = Object.values(state.units).filter(
    (u) => u.owner !== player && !u.inside && !isHidden(ctx.rules, u)
  );
  // Enemy units attacking this city: lately damaged the player, seen, near it.
  const attackersOf = (city: Building) =>
    enemies.filter((u) => (revealed[u.id] ?? 0) > state.tick && nearCity(city, u) && seen(u));
  const cache = new Map<EntityId, Unit[]>();
  const attackersCached = (city: Building) => {
    let list = cache.get(city.id);
    if (!list) {
      list = attackersOf(city);
      cache.set(city.id, list);
    }
    return list;
  };
  const pick = (unit: Unit, city: Building): Unit | undefined => {
    if (!canHit(ctx.rules, unit.type, true)) return undefined;
    let best: Unit | undefined;
    let bestDist = Number.POSITIVE_INFINITY;
    for (const enemy of attackersCached(city)) {
      const dist = axialHexDistance(enemy.pos, unit.pos);
      if (dist > GUARD_RADIUS) continue;
      if (
        dist < bestDist ||
        (dist === bestDist && best && idNumber(enemy.id) < idNumber(best.id))
      ) {
        best = enemy;
        bestDist = dist;
      }
    }
    return best;
  };

  const units = Object.values(state.units)
    .filter((u) => u.owner === player && !u.inside && !u.auto && canAttack(ctx.rules, u))
    .sort((a, b) => mintOrder(a.id) - mintOrder(b.id));
  for (const unit of units) {
    const order = unit.order;
    if (order?.kind === 'attack' && order.guard !== undefined) {
      if (unit.stance) {
        stand(state, unit);
        continue;
      }
      const city = state.buildings[order.guard];
      if (!city || city.owner !== player) {
        stand(state, unit);
        continue;
      }
      const target = state.units[order.targetId];
      if (target && !target.inside && nearCity(city, target) && seen(target)) continue;
      const next = pick(unit, city);
      if (next) {
        unit.order = { kind: 'attack', targetId: next.id, guard: city.id };
        delete unit.pace;
      } else stand(state, unit);
      continue;
    }
    if (unit.stance || order || (unit.path && unit.path.length > 0)) continue;
    const city = state.buildings[owners.get(getHexTileMapKey(unit.pos)) ?? ''];
    if (!city || city.owner !== player || !holdsStock(ctx.rules, city)) continue;
    const target = pick(unit, city);
    if (target) unit.order = { kind: 'attack', targetId: target.id, guard: city.id };
  }
}

/** A guard with nothing left to fight: drops the order and stops where it is. */
function stand(state: SimState, unit: Unit): void {
  delete unit.order;
  redirect(state, unit, [unit.pos]);
}

export { GUARD_LEASH, GUARD_RADIUS, guard };
